import React from 'react';
import { Accordion, AccordionSummary, AccordionDetails, Box, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const WeightingSection = ({ data }) => (
    <Accordion defaultExpanded sx={{ borderRadius: 2, '&:before': { display: 'none' } }} elevation={0}
        className="sh-card" style={{ padding: 0 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}
            sx={{ px: 3, py: 1.25, '& .MuiAccordionSummary-content': { my: 1 } }}>
            <Typography className="sync-health-section-title">Weighting schemes — how "client size" feeds in</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 3, pb: 3, pt: 0 }}>
            <Box className="sh-defs-block" sx={{ background: 'transparent', border: 'none', p: 0 }}>
                <Typography sx={{ fontSize: 13.5, lineHeight: 1.55, mb: 1.5 }}>
                    By default, every client counts <b>equally</b> in the overall TL;DR metrics. But a failed night for
                    <b> Duval</b> (~128k students, ~60 runs/night) has a very different operational impact than a failed night for
                    <b> Caverna</b> (~640 students, 1 run/night). The <b>Weight clients by</b> radio in the filter dialog
                    re-weights the overall aggregates so bigger clients count proportionally more.{' '}
                    <i>Per-client ranking tables are unaffected</i> — each row's rate is self-contained and independent of how
                    other clients are weighted.
                </Typography>
                <Box className="sh-metric-def">
                    <Box><span className="name">Equal</span> (default) &nbsp;·&nbsp; every client = 1</Box>
                    <Box>Treats every client alike, regardless of size. Good baseline for "how did our integrations perform on average".</Box>
                </Box>
                <Box className="sh-metric-def orange">
                    <Box><span className="name">Students</span> &nbsp;·&nbsp; weight ∝ enrollment</Box>
                    <Box>Turns the overall rate into a student-impact proxy: what fraction of <i>student-nights</i> got a clean sync? Duval's 128k students dominate.</Box>
                    <Box className="formula">raw[c] = max(1, students[c]) → weight[c] = raw[c] / mean(raw[...])</Box>
                </Box>
                <Box className="sh-metric-def orange">
                    <Box><span className="name">Jobs</span> &nbsp;·&nbsp; weight ∝ average jobs per active night</Box>
                    <Box>Jobs = individual pipeline work units (one per dataset per night), before the run collapse.
                        A PowerSchool extract for one client is ~30 jobs; a Canvas extract is ~4. We use the average per active night
                        (not total across the span) so a client that joined recently isn't penalized for having fewer observed nights.</Box>
                    <Box className="formula">raw[c] = max(1, avg_jobs_per_night[c]) → weight[c] = raw[c] / mean(raw[...])</Box>
                </Box>
                <Box className="sh-metric-def orange">
                    <Box><span className="name">Providers</span> &nbsp;·&nbsp; weight ∝ distinct providers this client uses</Box>
                    <Box>Larger portfolios (many providers) have more integration surface area; this scheme gives them more voice.</Box>
                    <Box className="formula">raw[c] = max(1, distinct_providers[c]) → weight[c] = raw[c] / mean(raw[...])</Box>
                </Box>
                <Box className="sh-metric-def red">
                    <Box><span className="name">Composite</span> &nbsp;·&nbsp; geometric mean of all three factors</Box>
                    <Box>A balanced "total importance" metric that won't zero-out when one factor is missing. Each factor is floored at 1 before combination so missing data doesn't kill the weight.</Box>
                    <Box className="formula">raw[c] = (max(1, students[c]) · max(1, jobs[c]) · max(1, providers[c]))^(1/3) → weight[c] = raw[c] / mean(raw[...])</Box>
                </Box>
                <Typography sx={{ mt: 2, mb: 1, fontWeight: 700, color: '#264653', fontSize: 14 }}>
                    Per-client weights at a glance
                </Typography>
                {/* Pre-rendered Python-side via build_weights_table_html — render verbatim. */}
                <Box
                    sx={{ overflowX: 'auto', '& table': { fontSize: 12 }, '& td.bad': { color: '#e76f51', fontWeight: 600 }, '& td.meh': { color: '#888' } }}
                    dangerouslySetInnerHTML={{ __html: data.weightsTableHtml || '' }}
                />
            </Box>
        </AccordionDetails>
    </Accordion>
);

export default WeightingSection;
